'use client';

import {
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { mapCtx } from './MapContext';
import { MapMarkerProps } from './types';
import { useActiveControl, useInvariantCallbackRef } from '../../../hooks';
import { MapMarkerInfo } from './MapMarkerInfo';

export const MapMarker: FC<PropsWithChildren<MapMarkerProps>> = ({
  markerOptions,
  markerInfoOptions,
  position,
  content,
  icon,
  zIndex,
  children,
  onClick,
  showInfo,
  isUpdatedMapPin
}) => {
  const map = useContext(mapCtx);
  const [marker, setMarker] =
    useState<google.maps.marker.AdvancedMarkerElement | null>(null);

  const onClickRef = useInvariantCallbackRef(onClick);
  const markerOptionsRef = useRef(markerOptions);
  useLayoutEffect(() => {
    if (!map || !google.maps.marker) return;

    const newMarker = new google.maps.marker.AdvancedMarkerElement({
      ...markerOptionsRef.current,
      map
    });
    setMarker(newMarker);

    const handleClick = () => onClickRef.current();

    const clickListener = newMarker.addListener('click', handleClick);
    return () => {
      clickListener?.remove();
      newMarker.map = null;
    };
  }, [map, onClickRef]);

  useLayoutEffect(() => {
    if (marker && position) marker.position = position;
  }, [marker, position]);

  useLayoutEffect(() => {
    if (marker && content) {
      marker.content = content;
    } else if (marker && icon) {
      const iconElement = document.createElement('img');
      iconElement.src = icon;
      marker.content = iconElement;
    }
  }, [marker, content, icon]);

  useLayoutEffect(() => {
    if (marker) marker.zIndex = zIndex;
  }, [marker, zIndex]);

  // Handle MarkerInfo visibility
  const showMarkerInfo = !!children;
  const { isActive, open, close } = useActiveControl({
    initialActive: showInfo && showMarkerInfo
  });

  useLayoutEffect(() => {
    // if showInfo not exist, use marker click and map click to handle MarkerInfo visibility
    if (!map || !marker || !showMarkerInfo || typeof showInfo === 'boolean')
      return;

    const showInfoListener = marker.addListener('click', open);
    const hideInfoListener = map.addListener('click', close);
    return () => {
      showInfoListener.remove();
      hideInfoListener.remove();
    };
  }, [close, map, marker, open, showInfo, showMarkerInfo]);

  useEffect(() => {
    // if showInfo exist, use this prop to handle MarkerInfo visibility
    if (!map || !marker || !showMarkerInfo || typeof showInfo !== 'boolean')
      return;

    if (showInfo) open();
    else close();
  }, [close, map, marker, open, showInfo, showMarkerInfo]);

  if (!showMarkerInfo) return null;

  return (
    !isUpdatedMapPin && (
      <MapMarkerInfo
        marker={marker}
        visible={isActive}
        onClose={close}
        markerInfoOptions={markerInfoOptions}
      >
        {children}
      </MapMarkerInfo>
    )
  );
};
